import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-parchment">
      {/* Background Texture & Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=2070&auto=format&fit=crop" 
          alt="Expedition backdrop" 
          className="w-full h-full object-cover opacity-30 mix-blend-multiply"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-parchment/20 via-transparent to-parchment"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full grid lg:grid-cols-12 gap-12 items-center">
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="h-px w-8 bg-amber"></span>
            <span className="stamp-badge !border-none !p-0 !rotate-0 opacity-80">Est. 2023 / Expedition Grade</span>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-bold leading-[0.9] mb-8 text-cobalt">
            The Science of <br />
            <span className="italic font-serif text-amber tracking-tight">Packing Light.</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-ink-light mb-12 font-light leading-relaxed max-w-xl">
            Precision-engineered modular systems for those who view travel as a workspace, not just a destination.
          </p>

          <div className="flex flex-col sm:flex-row gap-5">
            <button className="bg-cobalt text-white px-10 py-5 font-bold tracking-[0.15em] uppercase text-xs hover:bg-amber transition-all duration-300 flex items-center justify-center gap-3 group shadow-lg shadow-cobalt/20">
              View Collection
              <SafeIcon icon={FiIcons.FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="bg-transparent border-2 border-cobalt/20 text-cobalt px-10 py-5 font-bold tracking-[0.15em] uppercase text-xs hover:border-cobalt transition-all duration-300">
              The Philosophy
            </button>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="lg:col-span-5 relative hidden lg:block"
        >
          <div className="photo-frame relative z-10 rotate-2 hover:rotate-0 transition-transform duration-700">
            <img 
              src="https://images.unsplash.com/photo-1523206489230-c012c64b2b48?q=80&w=1000&auto=format&fit=crop" 
              alt="NomadCrate core" 
              className="w-full aspect-[4/5] object-cover"
            />
            <div className="absolute top-4 right-4 bg-amber text-white p-3 rounded-full shadow-lg">
              <SafeIcon icon={FiIcons.FiAward} className="text-2xl" />
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute -top-10 -right-10 w-40 h-40 border border-cobalt/10 rounded-full -z-10 animate-pulse"></div>
          <div className="absolute -bottom-6 -left-6 w-full h-full border-2 border-amber/20 -z-10 -rotate-3"></div>

          <motion.div 
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute -bottom-10 -right-4 bg-white p-6 journal-border max-w-[200px] hidden xl:block"
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber mb-2">Field Report</p>
            <p className="text-xs italic font-serif leading-snug">"The Atlas system saved 40% more space than traditional methods."</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Scroll</span>
        <div className="w-px h-12 bg-cobalt"></div>
      </motion.div>
    </section>
  );
};

export default Hero;